import { Anchor, Box, Grid, Group, Stack, Text, useMantineColorScheme } from "@mantine/core";
import { Link } from "react-router";

import { EmptyState } from "../EmptyState";
import { TextSerif } from "../TextSerif";
import { PerformanceBar } from "./components";

import type { MantineStyleProps } from "@mantine/core";
import type { FC, ReactNode } from "react";

export type BarData = {
  index?: number;
  label: string;
  value: number;
  to?: string;
};

export type PerformanceBarsProps = {
  align?: "left" | "right";
  data?: BarData[];
  formatValue?: (value: number) => string;
  renderLabel?: (item: BarData) => ReactNode;
  renderValue?: (item: BarData) => ReactNode;
  valueColumnWidth?: number | string;
  showBars?: boolean;
  header?: {
    left?: string;
    right?: string;
  };
  isEmptyState?: boolean;
} & MantineStyleProps;

const defaultFormatValue = (value: number) => `${value}`;

export const PerformanceBars: FC<PerformanceBarsProps> = ({
  align = "right",
  data = [],
  formatValue = defaultFormatValue,
  renderLabel,
  renderValue,
  valueColumnWidth,
  showBars = true,
  header,
  isEmptyState = false,
  ...props
}) => {
  const fontSize = { base: "xs", sm: "sm" };

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const color = isDark ? "gray.4" : "secondary.4";
  const textColor = isDark ? "#c9c9c9" : "secondary.4";

  if (data.length === 0) {
    return <EmptyState gutter={40} type="noCars" {...props} />;
  }

  const maxValue = Math.max(...(data.map((d) => Math.abs(d.value)) || 1));

  const scaleLog = (val: number, threshold = 10) => {
    const safeVal = Math.max(Math.abs(val), 1e-6);

    if (safeVal < threshold) {
      return Math.log(safeVal + 1) / Math.log(maxValue + 1);
    }

    return safeVal / maxValue;
  };

  return (
    <Grid w="100%" {...props}>
      <Grid.Col span="auto" style={{ overflow: "hidden" }}>
        <Stack gap="md">
          {header?.left && (
            <TextSerif size="sm" c={textColor} ta="left">
              {header.left}
            </TextSerif>
          )}
          <Box>
            {data.map((item, idx) => (
              <Group key={idx} align="center" gap="sm" wrap="nowrap" h={38}>
                {item.index !== undefined && (
                  <Text size="xs" fw="lighter" c={color}>
                    {item.index}.
                  </Text>
                )}
                {renderLabel ? (
                  renderLabel(item)
                ) : item.to ? (
                  <Anchor fz="sm" component={Link} to={item.to} title={item.label} truncate>
                    {item.label}
                  </Anchor>
                ) : (
                  <Text truncate fz={fontSize} c={color}>
                    {item.label}
                  </Text>
                )}
              </Group>
            ))}
          </Box>
        </Stack>
      </Grid.Col>
      <Grid.Col span="content" w={valueColumnWidth}>
        <Stack gap="md">
          {header?.right && (
            <TextSerif size="xs" c={textColor} ta="right">
              {header.right}
            </TextSerif>
          )}
          <Box>
            {data.map((item, idx) => (
              <PerformanceBar
                key={idx + item.label}
                align={align}
                label={renderValue ? renderValue(item) : formatValue(item.value)}
                value={item.value / maxValue}
                logarithmicValue={scaleLog(item.value, 10)}
                stretch={Boolean(renderValue && valueColumnWidth)}
                showBar={showBars}
                isEmptyState={isEmptyState}
              />
            ))}
          </Box>
        </Stack>
      </Grid.Col>
    </Grid>
  );
};
